import styled from 'styled-components';
import { Link } from 'react-router-dom';


const HomeContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0;
  
  @media (max-width: 768px) {
    padding: 0;
  }
`;

const HeroSection = styled.section`
  text-align: center;
  margin: 0 0 3rem;
  padding: 6rem 1rem;
  background: var(--gradient-primary);
  color: var(--white);
  position: relative;
  overflow: hidden;
  border-radius: 0 0 3rem 3rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  
  @media (max-width: 768px) {
    margin: 0 0 2rem;
    padding: 4rem 0.5rem;
    border-radius: 0 0 2rem 2rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('/images/pattern.svg') repeat;
    opacity: 0.1;
    animation: slide 20s linear infinite;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.2), transparent);
    pointer-events: none;
  }

  h1 {
    font-size: clamp(2rem, 5vw, 3.5rem);
    margin-bottom: 1.5rem;
    position: relative;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    letter-spacing: -0.02em;
  }

  p {
    font-size: clamp(1rem, 2vw, 1.2rem);
    margin-bottom: 2rem;
    opacity: 0.9;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
  }
`;

const ContentSection = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 4rem 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin: 2rem 0;
  }
`;

const Card = styled.div`
  background: var(--white);
  padding: 2rem;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  transition: all var(--transition-speed) var(--transition-bounce);
  display: flex;
  flex-direction: column;
  height: 100%;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    text-align: center;
  }

  h2 {
    color: var(--primary);
    margin-bottom: 1rem;
    font-size: clamp(1.25rem, 3vw, 1.5rem);
  }

  p {
    color: var(--gray);
    margin-bottom: 1.5rem;
    font-size: clamp(0.9rem, 2vw, 1rem);
    line-height: 1.6;
    flex-grow: 1;
  }
`;

const CTAButton = styled(Link)`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background: var(--gradient-primary);
  color: var(--white);
  border-radius: var(--radius-full);
  font-weight: 500;
  transition: all var(--transition-speed) var(--transition-bounce);
  align-self: center;
  
  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
    padding: 1rem;
  }

  &:hover {
    transform: var(--hover-lift-sm);
    box-shadow: var(--shadow-md);
  }
`;

const StatsSection = styled.section`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin: 4rem auto;
  padding: 2rem;
  background: var(--white);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  max-width: 1000px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: 1.5rem;
    margin: 2rem auto;
  }
`;

const StatItem = styled.div`
  text-align: center;
  padding: 1rem;
  
  .value {
    font-size: clamp(2rem, 5vw, 3rem);
    font-weight: bold;
    color: var(--primary);
    margin-bottom: 0.5rem;
  }

  .label {
    font-size: clamp(0.9rem, 2vw, 1rem);
    color: var(--gray);
  }
`;

const NextGameSection = styled.div`
  margin-top: 2rem;
  padding: 2rem;
  background: transparent;
  position: relative;
  z-index: 1;

  h2 {
    font-size: 1.8rem;
    margin-bottom: 2rem;
    color: var(--white);
    font-weight: 600;
  }

  .game-details {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }

  .teams-container {
    display: flex;
    align-items: center;
    gap: 2rem;
    justify-content: center;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 1rem;
    }
  }

  .team {
    display: flex;
    align-items: center;
    gap: 1rem;

    img {
      width: 45px;
      height: 45px;
      object-fit: contain;
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
    }

    .name {
      font-size: 1.3rem;
      font-weight: 600;
      color: var(--white);
    }
  }

  .versus {
    font-size: 1.3rem;
    font-weight: 600;
    color: var(--white);
    opacity: 0.9;
  }

  .location-container {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1.3rem;
    font-weight: 600;
    color: var(--white);
    margin-top: 0.5rem;

    .at-symbol {
      opacity: 0.8;
    }

    .venue {
      color: var(--white);
    }
  }

  .countdown {
    display: flex;
    justify-content: center;
    gap: 3rem;
    margin-top: 2.5rem;
    padding-top: 2rem;
    border-top: 1px solid rgba(255, 255, 255, 0.15);

    @media (max-width: 768px) {
      gap: 1.5rem;
    }
  }

  .countdown-item {
    text-align: center;
    min-width: 70px;

    .value {
      font-size: 2rem;
      font-weight: bold;
      color: var(--white);
      margin-bottom: 0.3rem;
    }

    .label {
      font-size: 0.9rem;
      color: var(--white);
      opacity: 0.8;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
  }
`;

const LetterAvatar = styled.div`
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--white);
  text-transform: uppercase;
  border: 2px solid rgba(255, 255, 255, 0.3);
`;

export { HomeContainer, HeroSection, ContentSection, Card, CTAButton, StatsSection, StatItem, NextGameSection, LetterAvatar };